import { layoutRect } from "../utils/displayDensity";
/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { SkeletonMarketViewBubbleChart } from "./skeletons/SkeletonMarketViewBubbleChart";
import DiscordBubbleTooltip from "./DiscordBubbleTooltip";
import {
  BUBBLE_LABEL_FONT,
  makeMemberColorScale,
  toBubbleDatum,
} from "./discordBubbleUtils";

export default function DiscordMarketView({ servers, loading }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [zoomT, setZoomT] = useState(d3.zoomIdentity);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = layoutRect(el);
      setDims({ w: Math.max(300, rect.width), h: Math.max(300, rect.height) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colorScale = useMemo(() => makeMemberColorScale(servers), [servers]);

  const nodes = useMemo(() => {
    const { w, h } = dims;
    if (!servers?.length || w <= 0 || h <= 0) return [];

    const children = servers
      .map((s) => toBubbleDatum(s, colorScale))
      .filter((c) => c.value > 0);
    if (!children.length) return [];

    // Pack into a centered square with a safe inset so the outer ring never clips.
    const inset = Math.floor(Math.min(w, h) * 0.06);
    const size = Math.max(1, Math.min(w, h) - inset * 2);
    const root = d3.hierarchy({ children }).sum((d) => d.value);
    d3.pack().size([size, size]).padding(1.5)(root);

    const dx = Math.floor((w - size) / 2);
    const dy = Math.floor((h - size) / 2);
    const leaves = root.leaves();
    for (const n of leaves) {
      n.x += dx;
      n.y += dy;
    }
    return leaves;
  }, [servers, dims, colorScale]);

  useEffect(() => {
    if (!svgRef.current || !nodes.length) return;
    const svg = d3.select(svgRef.current);
    const zoom = d3
      .zoom()
      .scaleExtent([0.5, 12])
      .on("zoom", ({ transform }) => {
        requestAnimationFrame(() => setZoomT(transform));
        d3.select("#discord-mm-zoom").attr("transform", transform);
      });
    svg.call(zoom);
    return () => svg.on(".zoom", null);
  }, [nodes]);

  const showSkeleton = loading || dims.w <= 0 || (!nodes.length && servers?.length > 0);
  const isEmpty = !loading && !nodes.length && !(servers?.length > 0);

  return (
    <div className="discord-market" ref={containerRef}>
      {isEmpty ? (
        <div className="discord-empty">No visible Discord servers match this filter.</div>
      ) : showSkeleton ? (
        <div className="discord-market-skeleton">
          <SkeletonMarketViewBubbleChart
            width={Math.max(480, Math.min(Math.min(dims.w || 1200, dims.h || 720), 900))}
            height={Math.max(480, Math.min(Math.min(dims.w || 1200, dims.h || 720), 900))}
            bubbleCount={200}
          />
        </div>
      ) : (
        <>
          <svg ref={svgRef} width={dims.w} height={dims.h} style={{ display: "block" }}>
            <g id="discord-mm-zoom">
              {nodes.map((d, i) => {
                const k = zoomT?.k || 1;
                const visibleR = d.r * k;
                const showLabel = d.r > 10 / Math.pow(k, 1.05) || visibleR >= 6;
                const maxChars = visibleR > 80 ? 28 : visibleR > 44 ? 22 : 12;
                const label =
                  d.data.name.length > maxChars
                    ? d.data.name.slice(0, maxChars) + "…"
                    : d.data.name;
                const fontSize = 10 / Math.pow(k, 0.8);
                const strokeW = Math.max(0.25, 1 / Math.pow(k, 0.9));
                const strokeA = Math.max(0.1, 0.5 / Math.pow(k, 0.85));
                const isHovered = hovered === d;

                return (
                  <g
                    key={d.data.server_id || i}
                    className="mm-node"
                    transform={`translate(${d.x},${d.y})`}
                    onMouseEnter={() => setHovered(d)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "grab" }}
                  >
                    <circle
                      r={isHovered ? d.r * 1.08 : d.r}
                      fill={d.data.color}
                      opacity={0.82}
                      style={{ transition: "all .25s ease" }}
                    />
                    {showLabel && (
                      <text
                        textAnchor="middle"
                        dy="0.35em"
                        fontSize={fontSize}
                        fill="#fff"
                        stroke={`rgba(0,0,0,${strokeA})`}
                        strokeWidth={strokeW}
                        style={{
                          pointerEvents: "none",
                          fontWeight: 500,
                          letterSpacing: "-0.2px",
                          paintOrder: "stroke",
                          fontFamily: BUBBLE_LABEL_FONT,
                        }}
                      >
                        {label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {hovered && (
            <DiscordBubbleTooltip
              datum={hovered.data}
              x={zoomT?.applyX?.(hovered.x) ?? hovered.x}
              y={zoomT?.applyY?.(hovered.y) ?? hovered.y}
              r={(hovered.r || 40) * (zoomT?.k || 1)}
              width={dims.w}
              height={dims.h}
            />
          )}
        </>
      )}
    </div>
  );
}
